import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, Pause, Square, SkipBack, SkipForward, Circle, 
  Repeat, Volume2, Activity, GripHorizontal, Minimize2, Maximize2
} from 'lucide-react';

// Pro Tools / Ableton-style Floating Transport
export const FloatingTransport = ({
  isPlaying,
  isRecording,
  position,
  tempo,
  timeSignature,
  metronomeOn,
  loopOn,
  loopStart,
  loopEnd,
  onPlay,
  onStop,
  onRecord,
  onRewind,
  onFastForward,
  onTempoChange,
  onMetronomeToggle,
  onLoopToggle,
  onLoopRegionChange,
  initialPosition = { x: window.innerWidth - 420, y: 80 },
  onPositionChange,
  minimized = false,
  onMinimizeToggle
}) => {
  const [pos, setPos] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const transportRef = useRef(null);
  
  // Handle drag start
  const handleDragStart = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    const rect = transportRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  
  // Handle drag
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      const newX = Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      setPos({ x: newX, y: newY });
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      onPositionChange?.(pos);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onPositionChange, pos]);
  
  // Parse position into bars.beats.ticks
  const [bars, beats, ticks] = position.split('.').map(Number);
  
  if (minimized) {
    return (
      <motion.div
        ref={transportRef}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="fixed z-50 bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-lg shadow-2xl border border-[#444] cursor-move select-none"
        style={{ left: pos.x, top: pos.y }}
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <GripHorizontal className="w-4 h-4 text-gray-500" />
          <button onClick={onPlay} className={`p-1.5 rounded ${isPlaying ? 'bg-green-500' : 'hover:bg-white/10'}`}>
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <span className="font-mono text-green-400 text-sm">{position}</span>
          <button onClick={onMinimizeToggle} className="p-1 hover:bg-white/10 rounded no-drag">
            <Maximize2 className="w-3 h-3" />
          </button>
        </div>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      ref={transportRef}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed z-50 select-none"
      style={{ left: pos.x, top: pos.y }}
      onMouseDown={handleDragStart}
    >
      {/* Pro Tools-style Transport Window */}
      <div className="bg-gradient-to-b from-[#3a3a3a] to-[#252525] rounded-lg shadow-2xl border border-[#555] overflow-hidden">
        {/* Title Bar */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-b from-[#4a4a4a] to-[#3a3a3a] border-b border-[#555] cursor-move">
          <div className="flex items-center gap-2">
            <GripHorizontal className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-300 uppercase tracking-wider">Transport</span>
          </div>
          <div className="flex items-center gap-1">
            {isRecording && (
              <span className="px-2 py-0.5 bg-red-500 rounded text-[10px] font-bold animate-pulse">REC</span>
            )}
            <button onClick={onMinimizeToggle} className="p-1 hover:bg-white/10 rounded no-drag">
              <Minimize2 className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        {/* Main Transport Controls */}
        <div className="px-4 py-3">
          {/* Counter Display - Pro Tools style */}
          <div className="flex items-center justify-center gap-6 mb-3">
            {/* Main Counter */}
            <div className="bg-[#0a0a0a] rounded-lg px-4 py-2 border border-[#333]">
              <div className="text-[10px] text-gray-500 text-center mb-0.5 uppercase">Bars | Beats | Ticks</div>
              <div className="flex items-center gap-1 font-mono">
                <span className="text-2xl font-bold text-green-400 w-10 text-right">{bars || 1}</span>
                <span className="text-xl text-gray-500">|</span>
                <span className="text-2xl font-bold text-green-400 w-6 text-center">{beats || 1}</span>
                <span className="text-xl text-gray-500">|</span>
                <span className="text-xl text-green-400/70 w-8">{String(ticks || 1).padStart(3, '0')}</span>
              </div>
            </div>
            
            {/* Tempo Display */}
            <div className="bg-[#0a0a0a] rounded-lg px-3 py-2 border border-[#333]">
              <div className="text-[10px] text-gray-500 text-center mb-0.5 uppercase">Tempo</div>
              <input
                type="number"
                value={tempo}
                onChange={(e) => onTempoChange?.(parseInt(e.target.value) || 120)}
                className="w-16 bg-transparent text-xl font-bold text-amber-400 text-center outline-none no-drag"
                min="40" max="240"
              />
            </div>
            
            {/* Time Signature */}
            <div className="bg-[#0a0a0a] rounded-lg px-3 py-2 border border-[#333]">
              <div className="text-[10px] text-gray-500 text-center mb-0.5 uppercase">Meter</div>
              <span className="text-xl font-bold text-blue-400">{timeSignature}</span>
            </div>
          </div>
          
          {/* Transport Buttons */}
          <div className="flex items-center justify-center gap-2">
            {/* Rewind */}
            <button
              onClick={onRewind}
              className="w-10 h-10 rounded-lg bg-gradient-to-b from-[#4a4a4a] to-[#3a3a3a] border border-[#555] hover:from-[#5a5a5a] hover:to-[#4a4a4a] flex items-center justify-center shadow-md active:shadow-inner no-drag"
            >
              <SkipBack className="w-5 h-5 text-gray-200" />
            </button>
            
            {/* Stop */}
            <button
              onClick={onStop}
              className="w-10 h-10 rounded-lg bg-gradient-to-b from-[#4a4a4a] to-[#3a3a3a] border border-[#555] hover:from-[#5a5a5a] hover:to-[#4a4a4a] flex items-center justify-center shadow-md active:shadow-inner no-drag"
            >
              <Square className="w-5 h-5 text-gray-200" />
            </button>
            
            {/* Play */}
            <button
              onClick={onPlay}
              className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-md active:shadow-inner no-drag transition-all ${
                isPlaying 
                  ? 'bg-gradient-to-b from-green-500 to-green-600 border-green-400' 
                  : 'bg-gradient-to-b from-[#4a4a4a] to-[#3a3a3a] border-[#555] hover:from-[#5a5a5a] hover:to-[#4a4a4a]'
              } border`}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-gray-200" />
              )}
            </button>
            
            {/* Fast Forward */}
            <button
              onClick={onFastForward}
              className="w-10 h-10 rounded-lg bg-gradient-to-b from-[#4a4a4a] to-[#3a3a3a] border border-[#555] hover:from-[#5a5a5a] hover:to-[#4a4a4a] flex items-center justify-center shadow-md active:shadow-inner no-drag"
            >
              <SkipForward className="w-5 h-5 text-gray-200" />
            </button>
            
            {/* Record */}
            <button
              onClick={onRecord}
              className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-md active:shadow-inner no-drag transition-all ${
                isRecording 
                  ? 'bg-gradient-to-b from-red-500 to-red-600 border-red-400 animate-pulse' 
                  : 'bg-gradient-to-b from-[#4a4a4a] to-[#3a3a3a] border-[#555] hover:from-red-900 hover:to-red-800'
              } border`}
            >
              <Circle className={`w-6 h-6 ${isRecording ? 'text-white fill-current' : 'text-red-400'}`} />
            </button>
          </div>
          
          {/* Secondary Controls */}
          <div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-[#333]">
            {/* Metronome */}
            <button
              onClick={onMetronomeToggle}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm no-drag transition-all ${
                metronomeOn 
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' 
                  : 'bg-[#1a1a1a] text-gray-500 border border-[#333] hover:text-gray-300'
              }`}
            >
              <Activity className="w-4 h-4" />
              <span className="text-xs font-medium">CLICK</span>
            </button>
            
            {/* Loop */}
            <button
              onClick={onLoopToggle}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm no-drag transition-all ${
                loopOn 
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50' 
                  : 'bg-[#1a1a1a] text-gray-500 border border-[#333] hover:text-gray-300'
              }`}
            >
              <Repeat className="w-4 h-4" />
              <span className="text-xs font-medium">LOOP</span>
            </button>
            
            {loopOn && (
              <div className="flex items-center gap-1 bg-[#0a0a0a] rounded px-2 py-1 border border-yellow-500/30 no-drag">
                <input
                  type="number"
                  value={loopStart}
                  onChange={(e) => onLoopRegionChange?.(Math.max(1, parseInt(e.target.value) || 1), loopEnd)}
                  className="w-8 bg-transparent text-yellow-400 text-center text-xs outline-none"
                  min="1"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  value={loopEnd}
                  onChange={(e) => onLoopRegionChange?.(loopStart, Math.max(loopStart + 1, parseInt(e.target.value) || loopStart + 1))}
                  className="w-8 bg-transparent text-yellow-400 text-center text-xs outline-none"
                  min={loopStart + 1}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FloatingTransport;
